"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import Link from "next/link";
import DebateMateLogo from "@/app/assets/images/dm_logo.svg";
import TextBubble from "./TextBubble";

// Animation configuration
const animationConfig = {
  initialPauseDuration: 500, // .5s
  logoFadeInDuration: 1500, // 1.5s
  logoFadeInPauseDuration: 600, // .6s
  logoShrinkDuration: 2500, // 2.5s
  logoShrinkPauseDuration: 600, // .6s 
  textBubbleAppearDuration: 100, // 0.1s (doesnt display so set to almost nothing)
  textBubbleAppearPauseDuration: 3000, // 3s (while text animates on)
  buttonsFadeInDuration: 600, // .6s
  logoShrinkFurtherDuration: 1200, // 1.2s for second shrink
  logoShrinkFurtherPauseDuration: 500, // .5s pause after second shrink
};

const initialTextBubbleMessage = "Let's snowball your confidence.";
const secondTextBubbleMessage = "Hey there. Ready to get started?";

export default function LandingPage() {
  const [showLogo, setShowLogo] = useState(false);
  const [logoWidth, setLogoWidth] = useState(280); // Initial width for animation
  const [showTextBubble, setShowTextBubble] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  const [textBubbleDisplayText, setTextBubbleDisplayText] = useState(initialTextBubbleMessage);
  const [hasButtonBeenClickedOnce, setHasButtonBeenClickedOnce] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const router = useRouter();

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];
    let delay = 0;

    // 1. Initial Pause
    delay = animationConfig.initialPauseDuration;
    timeouts.push(
      setTimeout(() => {
        // 2. Logo Fades In
        setShowLogo(true);
      }, delay),
    );

    // 3. Pause after logo fade-in starts, then start logo shrink
    delay += animationConfig.logoFadeInDuration; // Wait for fade-in to complete
    delay += animationConfig.logoFadeInPauseDuration;
    timeouts.push(
      setTimeout(() => {
        // 4. Logo Shrinks
        setLogoWidth(120);
      }, delay),
    );

    // 5. Pause after logo shrink starts, then show text bubble
    delay += animationConfig.logoShrinkDuration; // Wait for shrink to complete
    delay += animationConfig.logoShrinkPauseDuration;
    timeouts.push(
      setTimeout(() => {
        // 6. TextBubble Appears
        setShowTextBubble(true);
      }, delay),
    );

    // 7. Pause after text bubble appears, then show buttons
    // Note: textBubbleAppearDuration is very short, so this pause is effectively after text starts animating.
    delay += animationConfig.textBubbleAppearDuration; 
    delay += animationConfig.textBubbleAppearPauseDuration;
    timeouts.push(
      setTimeout(() => {
        // 8. Buttons Fade In
        setShowButtons(true);
      }, delay),
    );

    // 9. Animation Complete
    delay += animationConfig.buttonsFadeInDuration; // Wait for buttons to fade in
    timeouts.push(
      setTimeout(() => {
        setIsAnimationComplete(true);
      }, delay),
    );

    // Cleanup timeouts on component unmount
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  const handleContinueClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (isButtonDisabled) {
      event.preventDefault(); 
      return;
    }

    if (!hasButtonBeenClickedOnce) {
      event.preventDefault(); 
      setIsButtonDisabled(true);
      // Set logoWidth, the transition style will pick up the new duration
      setLogoWidth(42); 

      const totalDelayForSecondAnimationStep = animationConfig.logoShrinkFurtherDuration + animationConfig.logoShrinkFurtherPauseDuration;

      setTimeout(() => {
        setTextBubbleDisplayText(secondTextBubbleMessage);
        setHasButtonBeenClickedOnce(true);
        setIsButtonDisabled(false);
      }, totalDelayForSecondAnimationStep);
    }
  };

  // Determine current logo shrink duration based on animation phase
  const currentLogoShrinkDuration = 
    hasButtonBeenClickedOnce || isButtonDisabled // If second shrink is active or has happened
    ? animationConfig.logoShrinkFurtherDuration 
    : animationConfig.logoShrinkDuration;

  return (
    <div className="mainContent bg-primary text-white w-full h-full flex flex-col relative">
      {/* Div 1: Full width, content height, vertically centered */}
      <div className="w-full px-4 my-auto flex flex-col items-center">
        <DebateMateLogo
          style={{
            width: `${logoWidth}px`,
            opacity: showLogo ? 1 : 0,
            transition: `width ${currentLogoShrinkDuration}ms ease-in-out, opacity ${animationConfig.logoFadeInDuration}ms ease-in-out`,
          }}
        />

        {showTextBubble && (
          <TextBubble
            className={`bg-primary text-white border-none
            ${showTextBubble ? "opacity-100" : "opacity-0"}
            transition-opacity ease-in-out duration-[${animationConfig.textBubbleAppearDuration}ms]
          `}
            typographyClassName="text-xl text-center"
            animateFirstText
            maxBubbleHeight={300}
            animationMode="animateCharacters"
            displayText={textBubbleDisplayText}
          />
        )}
      </div>

      {/* Div 2: Full width, content height, anchored to bottom */}
      <div
        className={`
          buttons w-full px-4 pb-4 absolute bottom-0 flex flex-col
          ${showButtons ? "opacity-100" : "opacity-0"}
          transition-opacity ease-in-out duration-[${animationConfig.buttonsFadeInDuration}ms]
        `}
      >
        <Link
          className={`btn btn-secondary rounded-xl rounded-bl-none ${isButtonDisabled ? "btn-disabled" : ""}`}
          href="/deb/onboarding"
          onClick={handleContinueClick}
        >
          Continue
        </Link>
      </div>
    </div>
  );
}
